import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useEditor, EditorContent, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Table, TableRow, TableCell, TableHeader } from "@tiptap/extension-table";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, Strikethrough, List, ListOrdered, Table2, Trash2 } from "lucide-react";
import {
  normalizeRecipientList,
  resolveEmailBodyFromApi,
  getUserEmailFromStorage,
} from "Agentic Module/pages/emailTriaging/emailTriageUtils";
import PropTypes from "prop-types";

function isProbablyHtmlString(s) {
  if (!s || typeof s !== "string") return false;
  return /<[a-z][\s\S]*>/i.test(s.trim());
}

function normalizeEmailBodyString(s) {
  if (s == null) return "";
  if (typeof s !== "string") return String(s);
  return s
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n");
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function plainTextToEditorHtml(text) {
  const n = normalizeEmailBodyString(text);
  if (!n) return "<p></p>";
  return n
    .split(/\n/)
    .map((line) =>
      line === "" ? "<p><br></p>" : `<p>${escapeHtml(line)}</p>`,
    )
    .join("");
}

function valueToEditorHtml(value) {
  const n = normalizeEmailBodyString(value);
  if (!n) return "<p></p>";
  if (isProbablyHtmlString(n)) return n;
  return plainTextToEditorHtml(n);
}

function resolveInitialSubject(email) {
  return email?.email_sub ?? email?.mail_subject ?? "";
}

/** Prefer backend Cc; if empty, default to the logged-in user's email. */
function resolveEmailCc(rawCc) {
  const list = normalizeRecipientList(rawCc);
  if (list.length > 0) return list;
  const userEmail = String(getUserEmailFromStorage() || "").trim();
  return userEmail ? [userEmail] : [];
}

function applyInitialEmailToState(email, setters) {
  const row = email && typeof email === "object" ? email : {};
  setters.setEmailTo(normalizeRecipientList(row.email_to));
  setters.setEmailCc(resolveEmailCc(row.email_cc));
  setters.setEmailSubject(resolveInitialSubject(row));
  setters.setEmailBody(resolveEmailBodyFromApi(row));
}

/** Keeps focus in ProseMirror so parent sync does not run `setContent` and jump the caret. */
function toolbarMouseDown(e) {
  e.preventDefault();
}

function EditorToolbar({ editor }) {
  const state = useEditorState({
    editor,
    selector: (snapshot) => {
      const ed = snapshot.editor;
      if (!ed) {
        return {
          bold: false,
          italic: false,
          strike: false,
          bullet: false,
          ordered: false,
          inTable: false,
        };
      }
      return {
        bold: ed.isActive("bold"),
        italic: ed.isActive("italic"),
        strike: ed.isActive("strike"),
        bullet: ed.isActive("bulletList"),
        ordered: ed.isActive("orderedList"),
        inTable: ed.isActive("table"),
      };
    },
  });

  if (!editor) return null;

  const mkBtn = (label, active, onClick, icon, disabled = false) => (
    <button
      type="button"
      disabled={disabled}
      className={`composer-rich-toolbar-btn${active ? " composer-rich-toolbar-btn--active" : ""}${disabled ? " composer-rich-toolbar-btn--disabled" : ""}`}
      onMouseDown={toolbarMouseDown}
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      {icon}
    </button>
  );

  return (
    <div className="composer-rich-toolbar" role="toolbar" aria-label="Message formatting">
      <div className="composer-rich-toolbar-group">
        {mkBtn("Bold", state.bold, () => editor.chain().focus().toggleBold().run(), <Bold size={13} strokeWidth={2.25} />)}
        {mkBtn("Italic", state.italic, () => editor.chain().focus().toggleItalic().run(), <Italic size={13} strokeWidth={2.25} />)}
        {mkBtn("Strikethrough", state.strike, () => editor.chain().focus().toggleStrike().run(), <Strikethrough size={13} strokeWidth={2.25} />)}
      </div>
      <div className="composer-rich-toolbar-sep" aria-hidden />
      <div className="composer-rich-toolbar-group">
        {mkBtn("Bullet list", state.bullet, () => editor.chain().focus().toggleBulletList().run(), <List size={13} strokeWidth={2.25} />)}
        {mkBtn("Numbered list", state.ordered, () => editor.chain().focus().toggleOrderedList().run(), <ListOrdered size={13} strokeWidth={2.25} />)}
      </div>
      <div className="composer-rich-toolbar-sep" aria-hidden />
      <div className="composer-rich-toolbar-group">
        <button
          type="button"
          className="composer-rich-toolbar-btn"
          onMouseDown={toolbarMouseDown}
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run()
          }
          aria-label="Insert table"
          title="Insert 3×3 table (with header row)"
        >
          <Table2 size={13} strokeWidth={2.25} />
        </button>
        <button
          type="button"
          className={`composer-rich-toolbar-btn composer-rich-toolbar-btn--text${!state.inTable ? " composer-rich-toolbar-btn--disabled" : ""}`}
          disabled={!state.inTable}
          onMouseDown={state.inTable ? toolbarMouseDown : undefined}
          onClick={() => editor.chain().focus().addRowAfter().run()}
          aria-label="Add row below"
          title="Add row below"
        >
          +Row
        </button>
        <button
          type="button"
          className={`composer-rich-toolbar-btn composer-rich-toolbar-btn--text${!state.inTable ? " composer-rich-toolbar-btn--disabled" : ""}`}
          disabled={!state.inTable}
          onMouseDown={state.inTable ? toolbarMouseDown : undefined}
          onClick={() => editor.chain().focus().addColumnAfter().run()}
          aria-label="Add column right"
          title="Add column right"
        >
          +Col
        </button>
        <button
          type="button"
          className={`composer-rich-toolbar-btn composer-rich-toolbar-btn--text${!state.inTable ? " composer-rich-toolbar-btn--disabled" : ""}`}
          disabled={!state.inTable}
          onMouseDown={state.inTable ? toolbarMouseDown : undefined}
          onClick={() => editor.chain().focus().deleteRow().run()}
          aria-label="Delete row"
          title="Delete row"
        >
          −Row
        </button>
        <button
          type="button"
          className={`composer-rich-toolbar-btn composer-rich-toolbar-btn--text${!state.inTable ? " composer-rich-toolbar-btn--disabled" : ""}`}
          disabled={!state.inTable}
          onMouseDown={state.inTable ? toolbarMouseDown : undefined}
          onClick={() => editor.chain().focus().deleteColumn().run()}
          aria-label="Delete column"
          title="Delete column"
        >
          −Col
        </button>
        <button
          type="button"
          className={`composer-rich-toolbar-btn${!state.inTable ? " composer-rich-toolbar-btn--disabled" : ""}`}
          disabled={!state.inTable}
          onMouseDown={state.inTable ? toolbarMouseDown : undefined}
          onClick={() => editor.chain().focus().deleteTable().run()}
          aria-label="Delete table"
          title="Delete table"
        >
          <Trash2 size={13} strokeWidth={2.25} />
        </button>
      </div>
    </div>
  );
}

EditorToolbar.propTypes = {
  editor: PropTypes.shape({
    chain: PropTypes.func.isRequired,
  }),
};

const editorExtensions = [
  StarterKit.configure({
    heading: false,
    codeBlock: false,
    horizontalRule: false,
  }),
  Table.configure({
    resizable: true,
    allowTableNodeSelection: true,
  }),
  TableRow,
  TableHeader,
  TableCell,
  Placeholder.configure({
    placeholder:
      "Compose your message… Use the toolbar for bold, lists, and tables (resize columns from the table edge).",
  }),
];

const EmailDraftUI = forwardRef(function EmailDraftUI(
  {
    emailFrom,
    initialEmail,
    syncKey,
    emailTo: controlledTo,
    emailCc: controlledCc,
    emailSubject: controlledSubject,
    emailBody: controlledBody,
    setEmailTo,
    setEmailCc,
    setEmailSubject,
    setEmailBody,
    composerReadOnly = false,
  },
  ref,
) {
  const isControlled = typeof setEmailTo === "function";

  const [localTo, setLocalTo] = useState([]);
  const [localCc, setLocalCc] = useState([]);
  const [localSubject, setLocalSubject] = useState("");
  const [localBody, setLocalBody] = useState("");

  const emailTo = isControlled ? controlledTo : localTo;
  const emailCc = isControlled ? controlledCc : localCc;
  const emailSubject = isControlled ? controlledSubject : localSubject;
  const emailBody = isControlled ? controlledBody : localBody;

  const updateTo = isControlled ? setEmailTo : setLocalTo;
  const updateCc = isControlled ? setEmailCc : setLocalCc;
  const updateSubject = isControlled ? setEmailSubject : setLocalSubject;
  const updateBody = isControlled ? setEmailBody : setLocalBody;

  const prevSyncKey = useRef(syncKey);
  console.log(emailCc,"checking emailCc");

  useEffect(() => {
    if (isControlled) return;
    const syncKeyChanged = prevSyncKey.current !== syncKey;
    prevSyncKey.current = syncKey;
    if (!syncKeyChanged && syncKey == null) return;
    applyInitialEmailToState(initialEmail, {
      setEmailTo: setLocalTo,
      setEmailCc: setLocalCc,
      setEmailSubject: setLocalSubject,
      setEmailBody: setLocalBody,
    });
  }, [syncKey, initialEmail, isControlled]);

  // Controlled drafts: if backend Cc is empty on mount/remount, seed with logged-in user.
  // useEffect(() => {
  //   if (!isControlled || typeof setEmailCc !== "function") return;
  //   const list = normalizeRecipientList(controlledCc);
  //   if (list.length > 0) return;
  //   const fallback = resolveEmailCc([]);
  //   if (fallback.length === 0) return;
  //   setEmailCc(fallback);
  //   // Only on mount (parent remounts via key when loading a new draft/email).
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  useImperativeHandle(
    ref,
    () => ({
      getComposerData: () => ({
        emailTo: normalizeRecipientList(emailTo),
        emailCc: normalizeRecipientList(emailCc),
        emailSubject: emailSubject ?? "",
        emailBody: emailBody ?? "",
      }),
    }),
    [emailTo, emailCc, emailSubject, emailBody],
  );

  const editor = useEditor(
    {
      extensions: editorExtensions,
      content: valueToEditorHtml(emailBody),
      immediatelyRender: true,
      onUpdate: ({ editor: ed }) => {
        updateBody?.(ed.getHTML());
      },
      editorProps: {
        attributes: {
          class: "composer-tiptap-prosemirror",
          spellcheck: "true",
        },
      },
    },
    [],
  );

  useEffect(() => {
    if (!editor) return;
    const html = valueToEditorHtml(emailBody);
    if (editor.isFocused) return;
    const current = editor.getHTML();
    if (current === html) return;
    editor.commands.setContent(html, { emitUpdate: false });
  }, [emailBody, editor]);

  if (!editor) {
    return (
      <div className="composer-body-textarea composer-tiptap-placeholder">
        Loading editor…
      </div>
    );
  }

  return (
    <>
      <div className="composer-ribbon">
        <div className="composer-ribbon-row">
          <div className="composer-ribbon-labels">
            <span className="composer-ribbon-label-btn">
              From
              {/* <ChevronDown size={11} aria-hidden /> */}
            </span>
            <span className="composer-ribbon-label-btn">To</span>
            <span className="composer-ribbon-label-btn">Cc</span>
          </div>
          <div className="composer-ribbon-fields">
            <input
              type="email"
              value={emailFrom ?? ""}
              className="composer-ribbon-input"
              placeholder="your.email@company.com"
              disabled
            />
            <input
              type="text"
              value={Array.isArray(emailTo) ? emailTo.join(", ") : (emailTo || "")}
              onChange={(e) =>
                updateTo(
                  e.target.value
                    .split(",")
                    .map((v) => v.trim())
                    .filter(Boolean),
                )
              }
              readOnly={composerReadOnly}
              className="composer-ribbon-input"
              placeholder="recipient@example.com"
            />
            <input
              type="text"
              value={Array.isArray(emailCc) ? emailCc.join(", ") : (emailCc || "")}
              onChange={(e) =>
                updateCc(
                  e.target.value
                    .split(",")
                    .map((v) => v.trim())
                    .filter(Boolean),
                )
              }
              readOnly={composerReadOnly}
              className="composer-ribbon-input"
              placeholder="cc@example.com"
            />
          </div>
        </div>
        <div className="composer-ribbon-subject-row" style={{paddingBottom:'0px'}}>
          <span className="composer-ribbon-subject-label">Subject</span>
          <input
            type="text"
            value={emailSubject ?? ""}
            onChange={(e) => updateSubject(e.target.value)}
            readOnly={composerReadOnly}
            className="composer-ribbon-input"
            placeholder="Email subject"
            style={{border:'none'}}
          />
        </div>
      </div>
      <div className="composer-rich-editor">
        <EditorToolbar editor={editor} />
        <EditorContent editor={editor} className="composer-tiptap-editor" />
      </div>
    </>
  );
});

EmailDraftUI.propTypes = {
  emailFrom: PropTypes.string,
  initialEmail: PropTypes.object,
  syncKey: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  emailTo: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  emailCc: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  emailSubject: PropTypes.string,
  emailBody: PropTypes.string,
  setEmailTo: PropTypes.func,
  setEmailCc: PropTypes.func,
  setEmailSubject: PropTypes.func,
  setEmailBody: PropTypes.func,
  composerReadOnly: PropTypes.bool,
};

export default EmailDraftUI;
